type PslNS = typeof import("psl");
export type PslModule = Pick<PslNS, "parse" | "get">;

type PslParsed = {
  tld?: string;
  sld?: string;
  domain?: string;
  subdomain?: string;
};

function pickPslModule(ns: PslNS): PslModule {
  return "default" in ns
    ? (ns as unknown as { default: PslModule }).default
    : (ns as PslModule);
}

let _pslPromise: Promise<PslModule> | null = null;

async function loadPsl(): Promise<PslModule> {
  if (!_pslPromise) _pslPromise = import("psl").then(pickPslModule);

  return _pslPromise;
}

function toHostname(input?: string): string | null {
  if (!input) return null;

  const normalized = input.trim().replace(/\.$/, "").toLowerCase();

  try {
    const url = new URL(normalized);

    return url.hostname.toLowerCase();
  } catch {
    return normalized;
  }
}

/**
 * returns full subdomain (all before registrable domain).
 * examples:
 *   www.warrnambooldental.com.au -> "www"
 *   api.test.example.co.uk       -> "api.test"
 *   example.com                  -> null
 */
export async function getFullSubdomainAsync(
  fqdn?: string,
): Promise<string | null> {
  const host = toHostname(fqdn);
  if (!host) return null;

  const psl = await loadPsl();
  const parsed = psl.parse(host) as PslParsed;

  return parsed.subdomain ?? null;
}

/**
 * { subdomain: string ("@" if none), baseDomain: string (registrable) }
 *
 * examples:
 *  - "www.example.com.au"           -> { subdomain: "www",      baseDomain: "example.com.au" }
 *  - "api.test.example.co.uk"       -> { subdomain: "api.test", baseDomain: "example.co.uk" }
 *  - "example.com"                  -> { subdomain: "@",        baseDomain: "example.com" }
 *  - "https://mail.example.com/inbox"-> { subdomain: "mail",    baseDomain: "example.com" }
 */
export async function extractSubdomainAndBaseAsync(
  fqdn?: string,
): Promise<{ subdomain: string; baseDomain: string }> {
  const host = toHostname(fqdn);
  if (!host) return { subdomain: "@", baseDomain: "" };

  const psl = await loadPsl();

  const hasWildcard = host.startsWith("*.");

  // Psl validates "*"-wildcard subdomain as invalid
  // remove leading "*." so psl doesn't throw
  const filtered = host.replace(/^\*\./, "");
  const parsed = psl.parse(filtered) as PslParsed;

  // IMPORTANT: psl.get(host) will fail for wildcard hosts,
  // so always call get() on filtered hostname
  const baseDomain = psl.get(filtered) ?? parsed.domain ?? "";
  let subdomain = parsed.subdomain ?? "";

  if (hasWildcard) {
    // if rest had subdomain => "*.restSub"
    // else => "*"
    subdomain = subdomain ? `*.${subdomain}` : "*";
  }

  subdomain ||= "@";

  return { subdomain, baseDomain };
}
